import React from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import Icon, { IconSizes } from '@Components/Icon';
import Container from '@Components/layout/Container';
import GridCol from '@Components/layout/Grid/GridCol';
import GridRow from '@Components/layout/Grid/GridRow';
import Layout, { LayoutBackgrounds } from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import LoadingSpinner from '@Components/LoadingSpinner';
import PageHeader from '@Components/Page/PageHeader';
import PageTitle from '@Components/Page/PageTitle';
import Typography from '@Components/Typography';
import { MediaQueries } from '@Config/constants';
import { Messages } from '@Config/messages';
import { useGetFaqQuery } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { getCurrentLanguage } from '@Store/app/app.selectors';

import FAQCategoriesContentItem from './FAQCategoriesContentItem';
import FAQContentItem from './FAQContentItem';
import styles from './FAQPage.module.scss';
import MobileFaqContentItem from './MobileFaqCategory';

const FAQPage: React.FC = () => {
  const t = useTranslations();
  const currentLanguage = useSelector(getCurrentLanguage);

  const { data, loading } = useGetFaqQuery({
    variables: { language: currentLanguage },
  });

  const [activeCategory, setActiveCategory] = React.useState<number>(0);
  const [expandedQuestions, setExpandedQuestion] = React.useState<number[]>([]);

  const categoriesSorted = (data && [...data.faqs].sort((a, b) => a.category.orderNum - b.category.orderNum)) || [];
  const categories = categoriesSorted.map(category => category.category);

  const activeQuestions = categoriesSorted.find((_, index) => index === activeCategory)?.items;
  const isDesktop = useMediaQuery({ minWidth: MediaQueries.mobile });

  const contactUs = (
    <Row alignCenter constant className={styles.contactUs}>
      <div>
        <Icon icon="location-with-question-mark" size={IconSizes.s} />
      </div>
      <Typography msg={t(Messages.faqContactUs)} tag="span" className={styles.contactUsText} size="s" html />
    </Row>
  );

  const desktopView = (
    <GridRow>
      <GridCol size={4}>
        <ul className={styles.categoriesContent}>
          {categories.map((category, index) => (
            <FAQCategoriesContentItem
              category={category}
              key={index}
              active={index === activeCategory}
              onClick={() => {
                setActiveCategory(index);
                setExpandedQuestion([]);
              }}
            />
          ))}
          <div className={styles.separator} />
          {contactUs}
        </ul>
      </GridCol>
      <GridCol size={8}>
        <div className={styles.categoriesContent}>
          {activeQuestions && (
            <FAQContentItem
              questions={activeQuestions}
              expandedQuestions={expandedQuestions}
              setExpandedQuestion={setExpandedQuestion}
            />
          )}
        </div>
      </GridCol>
    </GridRow>
  );

  const mobileView = (
    <GridRow>
      <GridCol size={4}>
        <div className={styles.categoriesContent}>
          {categories.map((category, index) => (
            <MobileFaqContentItem
              category={category}
              key={index}
              active={index === activeCategory}
              onClick={() => {
                setActiveCategory(index);
                setExpandedQuestion([]);
              }}
            >
              {activeQuestions && index === activeCategory && (
                <FAQContentItem
                  questions={activeQuestions}
                  expandedQuestions={expandedQuestions}
                  setExpandedQuestion={setExpandedQuestion}
                />
              )}
            </MobileFaqContentItem>
          ))}
        </div>
      </GridCol>
    </GridRow>
  );

  const loadableContent = () => {
    if (loading || !data || !data.faqs) return <LoadingSpinner />;
    return isDesktop ? desktopView : mobileView;
  };

  return (
    <Layout background={LayoutBackgrounds.grayBackground} noTopPadding>
      <PageHeader>
        <Row justifyCenter column>
          <PageTitle msg={t(Messages.faqPage)} />
        </Row>
      </PageHeader>
      <Container className={styles.faqContentWrapper}>{loadableContent()}</Container>
    </Layout>
  );
};

export default FAQPage;
